import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { HttpService } from '../../core/services/http.service';
import { DropdownOption } from '../../components/ui/input/input-dropdown/input-dropdown.component';

interface OnboardingRequestPayload {
  hotel_name: string;
  hotel_city: string;
  hotel_country: string | null;
  hotel_website: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  contact_position: string | null;
  message: string | null;
  how_did_you_hear: string | null;
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  standalone: false,
})
export class RegisterComponent implements OnInit, OnDestroy {
  private langSub?: Subscription;
  requestForm!: FormGroup;
  isSubmitting = false;
  isSubmitted = false;
  submitted = false;

  countryDropdownOptions: DropdownOption[] = [];
  positionDropdownOptions: DropdownOption[] = [];
  referralDropdownOptions: DropdownOption[] = [];

  readonly countryOptions = [
    { value: 'CH', label: 'Switzerland' },
    { value: 'AT', label: 'Austria' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'LI', label: 'Liechtenstein' },
  ];

  readonly positionOptions = [
    { value: 'owner', label: 'ONBOARDING_REQUEST.POSITION_OPTIONS.OWNER' },
    { value: 'general_manager', label: 'ONBOARDING_REQUEST.POSITION_OPTIONS.GENERAL_MANAGER' },
    { value: 'front_office', label: 'ONBOARDING_REQUEST.POSITION_OPTIONS.FRONT_OFFICE' },
    { value: 'revenue', label: 'ONBOARDING_REQUEST.POSITION_OPTIONS.REVENUE' },
    { value: 'sales', label: 'ONBOARDING_REQUEST.POSITION_OPTIONS.SALES' },
    { value: 'other', label: 'ONBOARDING_REQUEST.POSITION_OPTIONS.OTHER' },
  ];

  readonly referralOptions = [
    { value: 'partner_hotel', label: 'ONBOARDING_REQUEST.REFERRAL_OPTIONS.PARTNER_HOTEL' },
    { value: 'event', label: 'ONBOARDING_REQUEST.REFERRAL_OPTIONS.EVENT' },
    { value: 'online', label: 'ONBOARDING_REQUEST.REFERRAL_OPTIONS.ONLINE' },
    { value: 'sales_contact', label: 'ONBOARDING_REQUEST.REFERRAL_OPTIONS.SALES_CONTACT' },
    { value: 'other', label: 'ONBOARDING_REQUEST.REFERRAL_OPTIONS.OTHER' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private httpService: HttpService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.buildDropdownOptions();
    this.langSub = this.translate.onLangChange.subscribe(() => this.buildDropdownOptions());
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private buildDropdownOptions(): void {
    this.countryDropdownOptions = this.countryOptions.map(o => ({ value: o.value, label: o.label }));

    this.positionDropdownOptions = this.positionOptions.map(o => ({
      value: o.value,
      label: this.translate.instant(o.label),
    }));

    this.referralDropdownOptions = this.referralOptions.map(o => ({
      value: o.value,
      label: this.translate.instant(o.label),
    }));
  }

  private initializeForm(): void {
    this.requestForm = this.formBuilder.group({
      // Hotel information
      hotelName: ['', [Validators.required, Validators.maxLength(255)]],
      hotelCity: ['', [Validators.required, Validators.maxLength(255)]],
      hotelCountry: ['CH'],
      hotelWebsite: ['', [Validators.maxLength(255)]],

      // Contact information
      contactName: ['', [Validators.required, Validators.maxLength(30)]],
      contactEmail: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      contactPhone: ['', [Validators.pattern(/^[+]?[0-9\s\-()]{6,20}$/)]],
      contactPosition: [null],

      // Additional information
      message: ['', [Validators.maxLength(2000)]],
      howDidYouHear: [null],
    });
  }

  onSubmit(): void {
    this.submitted = true;
    this.requestForm.markAllAsTouched();

    if (this.requestForm.invalid) {
      this.scrollToFirstError();
      return;
    }

    this.isSubmitting = true;

    const payload = this.buildPayload();

    this.httpService.post<{ success: boolean }>('v2/onboarding-requests', payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.isSubmitted = true;
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }

  private normalizeWebsiteUrl(url: string | null): string | null {
    if (!url || !url.trim()) {
      return null;
    }

    const trimmed = url.trim();

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }

    return `https://${trimmed}`;
  }

  private scrollToFirstError(): void {
    setTimeout(() => {
      const firstInvalid = document.querySelector('.ng-invalid:not(form):not(fieldset)');
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }

  private buildPayload(): OnboardingRequestPayload {
    const form = this.requestForm.value;

    return {
      hotel_name: form.hotelName,
      hotel_city: form.hotelCity,
      hotel_country: form.hotelCountry || null,
      hotel_website: this.normalizeWebsiteUrl(form.hotelWebsite),
      contact_name: form.contactName,
      contact_email: form.contactEmail,
      contact_phone: form.contactPhone || null,
      contact_position: form.contactPosition || null,
      message: form.message || null,
      how_did_you_hear: form.howDidYouHear || null,
    };
  }
}
